import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from './Icon';
import { CONTACT } from '../data/site';
import { reply, GREETING } from '../data/assistant';

/* BB Assistant — the answers live in src/data/assistant.js */
/* **bold** and *italic* */
function Formatted({ text }) {
  return text.split(/\*\*(.*?)\*\*/g).map((p, i) => (i % 2
    ? <strong key={i}>{p}</strong>
    : p.split(/\*(.*?)\*/g).map((q, j) => (j % 2 ? <em key={`${i}-${j}`}>{q}</em> : <React.Fragment key={`${i}-${j}`}>{q}</React.Fragment>))));
}

const ACTIONS = {
  call: { label: 'Call us', icon: 'phone', href: `tel:${String(CONTACT.phone).replace(/[^\d+]/g, '')}` },
  whatsapp: { label: 'WhatsApp', icon: 'chat', href: `https://wa.me/${CONTACT.whatsapp}`, external: true },
  form: { label: 'Get a free estimate', icon: 'doc', to: '/request-consultation' },
  map: { label: 'Get directions', icon: 'pin', href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`Blue Block ${CONTACT.address} ${CONTACT.city}`)}`, external: true },
};

const now = () => new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

export default function AIChat() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [msgs, setMsgs] = useState([{ from: 'bot', text: GREETING, time: now() }]);
  const bodyRef = useRef(null);
  const inputRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => { bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: 'smooth' }); }, [msgs, typing, open]);
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => {
    if (!open) return undefined;
    // focus the input on larger screens only (on phones it would pop the keyboard straight away)
    if (window.matchMedia('(min-width: 768px)').matches) setTimeout(() => inputRef.current?.focus(), 250);
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    document.body.classList.add('chat-open');
    return () => { window.removeEventListener('keydown', onKey); document.body.classList.remove('chat-open'); };
  }, [open]);

  const ask = (text) => {
    const q = text.trim();
    if (!q || typing) return;
    setMsgs((m) => [...m, { from: 'user', text: q, time: now() }]);
    setInput('');
    setTyping(true);
    timer.current = setTimeout(() => {
      const r = reply(q);
      setMsgs((m) => [...m, { from: 'bot', text: r.text, actions: r.actions, time: now() }]);
      setTyping(false);
    }, 600 + Math.random() * 450);
  };

  return (
    <>
      <button className="fab fab-chat" onClick={() => setOpen((o) => !o)} aria-label={open ? 'Close assistant' : 'Open assistant'} aria-expanded={open}>
        <Icon name={open ? 'close' : 'chat'} size={24} />
        {!open && <span className="fab-tip">Ask BB Assistant</span>}
      </button>

      {open && (
        <div className="chat" role="dialog" aria-label="BB Assistant">
          <div className="chat-head">
            <div className="chat-avatar">BB</div>
            <div className="chat-title"><strong>BB Assistant</strong><small>Type a question · instant reply</small></div>
            <button className="chat-close" onClick={() => setOpen(false)} aria-label="Close"><Icon name="close" size={16} /></button>
          </div>

          <div className="chat-body" ref={bodyRef} data-lenis-prevent>
            {msgs.map((m, i) => (
              <div key={i} className={`msg ${m.from}`}>
                <Formatted text={m.text} />
                {m.actions?.length > 0 && (
                  <span className="msg-actions">
                    {m.actions.map((k) => {
                      const a = ACTIONS[k];
                      if (!a) return null;
                      return a.to
                        ? <Link key={k} to={a.to} onClick={() => setOpen(false)}><Icon name={a.icon} size={14} />{a.label}</Link>
                        : <a key={k} href={a.href} {...(a.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}><Icon name={a.icon} size={14} />{a.label}</a>;
                    })}
                  </span>
                )}
                <time>{m.time}</time>
              </div>
            ))}
            {typing && <div className="typing"><i /><i /><i /></div>}
          </div>

          <form className="chat-input" onSubmit={(e) => { e.preventDefault(); ask(input); }}>
            <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type your question…" aria-label="Your question" maxLength={300} enterKeyHint="send" autoComplete="off" />
            <button type="submit" aria-label="Send" disabled={!input.trim() || typing}><Icon name="send" size={18} /></button>
          </form>
        </div>
      )}
    </>
  );
}
